import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { getIncomingMessages } from '../../../lib/api';
import { MediaMessage, isMediaMessage, mediaLabel } from '../components/MediaMessage';
import '../../report/pages/summary.css';
import '../../report/pages/detailsReport.css';
import './chat.css';

const MessagesList = () => {
  const iso = (d) => d.toISOString().slice(0,10);
  const today = useMemo(() => new Date(), []);
  const last30 = useMemo(() => { const d = new Date(); d.setDate(d.getDate()-30); return d; }, []);

  const [fromDate, setFromDate] = useState(iso(last30));
  const [toDate, setToDate] = useState(iso(today));
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const nullish = (v) => (v === null || v === undefined ? '-' : v);

  const messageBodyDisplay = (msg) => {
    const body = msg.message_body ?? msg.body ?? msg.message_content;
    if (body) return body;
    const type = msg.message_type ?? msg.type;
    if (type && String(type).toLowerCase() !== 'text') return `[${mediaLabel(type)}]`;
    return '-';
  };

  const fetchData = async (p = 1, append = false) => {
    if (loading) return;
    setLoading(true); setError('');
    try {
      const res = await getIncomingMessages(p, limit, fromDate, toDate);
      const list = res?.messages || res?.data || [];
      setRows((prev) => append ? [...prev, ...list] : list);
      setHasMore(list.length >= limit);
      setPage(p);
    } catch (e) {
      setError(e?.message || 'Failed to load messages');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(1, false); }, []);

  const onSearch = () => fetchData(1, false);
  const onLoadMore = () => { if (hasMore) fetchData(page+1, true); };

  const fmtTime = (t) => t ? new Date(t).toLocaleString() : '—';

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await getIncomingMessages(1, 50000, fromDate, toDate);
      const data = res?.messages || res?.data || [];
      if (!data.length) {
        alert('No messages to export');
        return;
      }

      const formatted = data.map((msg) => ({
        'Contact Name': nullish(msg.contact_name ?? msg.name),
        'WA ID': nullish(msg.wa_id ?? msg.recipient_id),
        'Message Type': nullish(msg.message_type ?? msg.type),
        'Message Body': nullish(
          (() => {
            const display = messageBodyDisplay(msg);
            return display === '-' ? null : display;
          })()
        ),
        'Media ID': nullish(msg.media_id),
        'Mime Type': nullish(msg.mime_type),
        Timestamp: nullish(
          (msg.timestamp || msg.message_timestamp || msg.created_at)
            ? new Date(msg.timestamp || msg.message_timestamp || msg.created_at).toLocaleString()
            : '-'
        ),
        Filename: nullish(msg.filename ?? msg.file_name),
      }));

      const worksheet = XLSX.utils.json_to_sheet(formatted);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Messages');
      XLSX.writeFile(workbook, 'messages.xlsx');
    } catch {
      alert('Failed to export messages');
    } finally {
      setExporting(false);
    }
  };

  const renderBodyCell = (r) => {
    if (isMediaMessage(r)) {
      return (
        <MediaMessage
          compact
          mediaId={r.media_id}
          messageType={r.message_type || r.type}
          mimeType={r.mime_type}
          caption={r.message_body || r.body || r.message_content || ''}
          filename={r.filename || r.file_name}
        />
      );
    }
    return r.message_body || r.body || r.message_content || '-';
  };

  return (
    <div className="summary-wrap">
      <div className="summary-header">
        <h2>Messages</h2>
        <div className="summary-meta">{rows.length} loaded</div>
      </div>

      <div className="filter-bar filter-bar--compact">
        <div className="field">
          <label>From</label>
          <input className="filt-control" type="date" value={fromDate} onChange={(e)=>setFromDate(e.target.value)} />
        </div>
        <div className="field">
          <label>To</label>
          <input className="filt-control" type="date" value={toDate} onChange={(e)=>setToDate(e.target.value)} />
        </div>
        <div className="actions filter-bar-actions">
          <button className="btn btn-primary" onClick={onSearch}>Search</button>
          <button
            type="button"
            className="summary-export-btn"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? 'Exporting…' : 'Export'}
          </button>
        </div>
      </div>

      {error && <div className="error" style={{ marginBottom: 8 }}>{error}</div>}

      <div className="summary-scroll">
        <div className="summary-table-wrap">
          <table className="summary-table">
            <thead>
              <tr>
                <th>Contact Name</th>
                <th>WA ID</th>
                <th>Message Type</th>
                <th>Message Body</th>
                <th>Media ID</th>
                <th>Mime Type</th>
                <th>Timestamp</th>
                <th>Filename</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                Array.from({length:8}).map((_,i)=> (
                  <tr key={`sk-${i}`} className="skeleton-row">
                    {Array.from({length:9}).map((_,j)=> (
                      <td key={j}><div className={`sk ${j===2 ? 'sk-pill' : 'sk-text'} ${j===3||j===8 ? 'long' : ''}`} /></td>
                    ))}
                  </tr>
                ))
              ) : (
                rows.map((r, idx) => (
                  <tr key={idx}>
                    <td data-label="Contact Name">{r.contact_name || r.name || '-'}</td>
                    <td data-label="WA ID">{r.wa_id || r.recipient_id || '-'}</td>
                    <td data-label="Message Type">{r.message_type || r.type || '-'}</td>
                    <td data-label="Message Body">{renderBodyCell(r)}</td>
                    <td data-label="Media ID">{r.media_id || '-'}</td>
                    <td data-label="Mime Type">{r.mime_type || '-'}</td>
                    <td data-label="Timestamp">{fmtTime(r.timestamp || r.message_timestamp || r.created_at)}</td>
                    <td data-label="Filename">{r.filename || r.file_name || '-'}</td>
                    <td data-label="Action">
                      { (r.wa_id || r.recipient_id) ? (
                        <Link className="btn btn-primary" to={`/main/chat/${r.wa_id || r.recipient_id}`}>Chat</Link>
                      ) : '-'}
                    </td>
                  </tr>
                ))
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={9} className="empty-cell">No messages found</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {hasMore && (
          <div className="pagination-controls" style={{ justifyContent: 'center' }}>
            <button className="btn" style={{ border: '1px solid var(--border)' }} onClick={onLoadMore} disabled={loading}>
              {loading ? 'Loading…' : 'Load More'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesList;
